/**
 * FeaturedSessionCard - the big card at the top of the Sessions dialog.
 *
 * Used for both featured variants: the current session ("Resume Last Session"
 * / "Go back to your session") and, when there is no current session, the most
 * recent saved one ("Reopen Last Session"). Shows the session name (renameable
 * in place), its connections as chips, and a preview of its tabs.
 *
 * The card itself is not a click target: its action lives on an explicit
 * button, the same way every card in the sessions list below it works. The
 * card body holds controls of its own (name editor, connection chips), so a
 * card-wide click made it ambiguous which click did what.
 */
import { RotateCcw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import type { Connection } from '../../../types/electron';
import type { Session } from '../types/sessionTypes';
import type { ConnectionInfo } from '../utils/sessionDisplayUtils';
import { formatTimeAgo } from '../utils/sessionDisplayUtils';
import {
  applyConnectionDecisions,
  createExclusionDecisions,
} from '../services/connectionValidationService';
import { connectionNeedsPasswordPrompt } from '../../connections/utils/connectPasswordPredicate';
import { SessionConnectionChips } from './SessionConnectionChips';
import { SessionNameEditor } from './SessionNameEditor';
import { SessionTabsPreview } from './SessionTabsPreview';

interface FeaturedSessionCardProps {
  /** The session this card represents. */
  session: Session;
  /** Headline above the session name (what the card's action will do). */
  title: string;
  /** Connections the session declares, resolved for display. */
  connections: ConnectionInfo[];
  /**
   * All saved connection configs. Needed to answer WHICH connection the resume
   * will open: a candidate whose config is gone, or that needs an Entra auth
   * window, is skipped by the restore, so the caption must skip it too.
   */
  savedConnections: Connection[];
  /** Connections the user switched off for the upcoming resume. */
  excludedConnectionIds: string[];
  /**
   * True when activating the card restores the session — the only case where
   * excluding a connection means anything. False when the session is already
   * live and the card merely closes the dialog.
   */
  canExcludeConnections: boolean;
  onToggleConnectionExcluded: (connectionId: string, excluded: boolean) => void;
  /** Primary action: resume/reopen the session (or return to it when live). */
  onActivate: () => void;
  /** Label of the action button — `Resume` | `Go back` | `Open`. */
  actionLabel: string;
  onRename: (name: string) => void;
  /** `resume-session-button` | `reopen-last-session-button` */
  testId: string;
}

export function FeaturedSessionCard({
  session,
  title,
  connections,
  savedConnections,
  excludedConnectionIds,
  canExcludeConnections,
  onToggleConnectionExcluded,
  onActivate,
  actionLabel,
  onRename,
  testId,
}: FeaturedSessionCardProps) {
  // Excluding a connection routes through the `remove` decision path, which
  // drops the connection's tabs with it — say so before the user commits.
  const excluded = new Set(canExcludeConnections ? excludedConnectionIds : []);

  // The session the resume will actually see, built the way App builds it:
  // exclusions applied first, so the tab count below describes the restore that
  // is queued up rather than the one the card started with.
  const effectiveSession = applyConnectionDecisions(
    session,
    createExclusionDecisions(Array.from(excluded), savedConnections)
  );
  // The resume opens every connection — except these, which would have to raise
  // the single-instance password prompt and therefore wait for the first tab
  // that needs them. Same predicate the resume itself uses, so the chip can't
  // promise what the resume won't do.
  const passwordConnectionIds = savedConnections
    .filter((c) => connectionNeedsPasswordPrompt(c))
    .map((c) => c.id);

  // Count what the exclusion actually costs by differencing the two tab lists,
  // rather than re-deciding "does this tab need an excluded connection?" here —
  // a second answer to that question is how the count and the resume drift
  // apart (compare tabs keep their endpoints in `content`, not `connectionInfo`).
  const excludedTabCount = canExcludeConnections
    ? session.tabs.length - effectiveSession.tabs.length
    : 0;

  return (
    <div
      className="w-full p-4 rounded-lg border border-border bg-card text-left animate-in fade-in slide-in-from-bottom-2 duration-200"
      data-testid={testId}
    >
      <div className="flex items-start gap-3">
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary shrink-0">
          <RotateCcw className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center justify-between">
            <span className="font-medium" data-testid="session-name">
              {title}
            </span>
            <span className="text-xs text-muted-foreground">
              {formatTimeAgo(session.lastOpenedAt)}
            </span>
          </div>
          <div className="text-sm">
            <SessionNameEditor
              name={session.name}
              customName={session.customName}
              onRename={onRename}
              testId="featured-session-name"
              inputTestId="featured-session-name-input"
            />
          </div>

          <SessionConnectionChips
            connections={connections}
            excludedConnectionIds={excludedConnectionIds}
            canExclude={canExcludeConnections}
            excludedTabCount={excludedTabCount}
            passwordConnectionIds={passwordConnectionIds}
            onToggleExcluded={onToggleConnectionExcluded}
          />

          <SessionTabsPreview tabs={session.tabs} className="mt-2" />
        </div>

        <Button
          size="sm"
          className="shrink-0 self-center active:scale-[0.96] transition-[color,box-shadow,scale]"
          onClick={onActivate}
          data-testid="featured-session-open-button"
        >
          {actionLabel}
        </Button>
      </div>
    </div>
  );
}
